import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowUpRight, ArrowRight, Radar, Users, Headphones, PackageSearch, MapPin,
  ShieldCheck, Building2, Wallet, Quote, CheckCircle2, Gauge,
} from "lucide-react";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "@/components/ui/accordion";
import { Reveal } from "@/components/Reveal";
import { CountUp } from "@/components/CountUp";
import { useLang } from "@/context/LanguageContext";
import { APP_URL } from "@/i18n/translations";

const IMG = {
  truckHero: "https://static.prod-images.emergentagent.com/jobs/1e2b309f-a2b6-4e47-9dc1-3b5480601160/images/2597a320691684c369f0a6fdd6978d8435589db89bf798dbce9b1fb63b3079f7.jpeg",
  truckSide: "https://static.prod-images.emergentagent.com/jobs/1e2b309f-a2b6-4e47-9dc1-3b5480601160/images/db0b1c0ae178b328c2c2f087ed0818c57a44bc582396a51acf3591b281b3c299.jpeg",
  truckTop: "https://static.prod-images.emergentagent.com/jobs/1e2b309f-a2b6-4e47-9dc1-3b5480601160/images/f4b8ed6cfd5321126203337ed9bdf22b8e6fd253de694eaa74a670c8d30276fc.jpeg",
  container: "https://static.prod-images.emergentagent.com/jobs/1e2b309f-a2b6-4e47-9dc1-3b5480601160/images/e8cb4b13ac9fc2c0a02fd412942cd535adf04f3ce16dbb7925154ba039b02e30.jpeg",
  highway: "https://images.pexels.com/photos/10268770/pexels-photo-10268770.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600",
  warehouse: "https://static.prod-images.emergentagent.com/jobs/1e2b309f-a2b6-4e47-9dc1-3b5480601160/images/da3c280b9cc492770ceb467108b8853f84de3f364448dfb549a345df43202302.jpeg",
};

const CITIES = "İSTANBUL · ANKARA · İZMİR · BURSA · ANTALYA · ADANA · KONYA · GAZİANTEP · MERSİN · KAYSERİ · SAMSUN · TRABZON";
const SERVICE_ICONS = [PackageSearch, MapPin, Wallet, Users, Building2, Radar];
const FEATURE_ICONS = [Radar, Users, Headphones];

const lineParent = { hidden: {}, show: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } } };
const lineChild = { hidden: { y: "110%" }, show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } } };

function Speedometer({ label }) {
  const R = 84, C = Math.PI * R; // half circle
  return (
    <Reveal className="flex flex-col items-center lg:items-end">
      <div className="relative w-[220px] h-[130px]">
        <svg viewBox="0 0 200 120" className="w-full h-full">
          <path d="M16 110 A84 84 0 0 1 184 110" fill="none" stroke="#1f1f22" strokeWidth="10" strokeLinecap="round" />
          <motion.path
            d="M16 110 A84 84 0 0 1 184 110" fill="none" stroke="#FF3B30" strokeWidth="10" strokeLinecap="round"
            strokeDasharray={C} initial={{ strokeDashoffset: C }} whileInView={{ strokeDashoffset: C * 0.06 }}
            viewport={{ once: true }} transition={{ duration: 1.8, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-end pb-1">
          <div className="font-display font-black text-5xl text-white tracking-tighter flex items-baseline gap-1">
            <CountUp value={98.2} suffix="" /><span className="text-[#FF3B30] text-2xl">%</span>
          </div>
          <div className="flex items-center gap-1.5 text-white/45 text-[11px] tracking-widest mt-1">
            <Gauge size={12} /> {label}
          </div>
        </div>
      </div>
    </Reveal>
  );
}

export default function Home() {
  const { t } = useLang();
  const h = t.home;

  const heroRef = useRef(null);
  const { scrollYProgress: heroP } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroTruckX = useTransform(heroP, [0, 1], ["0%", "18%"]);
  const heroTruckScale = useTransform(heroP, [0, 1], [1, 1.12]);
  const heroTruckOpacity = useTransform(heroP, [0, 0.9], [1, 0]);

  const opsRef = useRef(null);
  const { scrollYProgress: opsP } = useScroll({ target: opsRef, offset: ["start start", "end end"] });
  const driveY = useTransform(opsP, [0, 1], ["-12%", "44%"]);
  const driveRotate = useTransform(opsP, [0, 1], [-2.5, 2.5]);
  const wmX = useTransform(opsP, [0, 1], ["3%", "-26%"]);
  const cap0 = useTransform(opsP, [0.02, 0.12, 0.3, 0.38], [0, 1, 1, 0]);
  const cap1 = useTransform(opsP, [0.38, 0.48, 0.62, 0.7], [0, 1, 1, 0]);
  const cap2 = useTransform(opsP, [0.7, 0.8, 1, 1], [0, 1, 1, 1]);
  const caps = [cap0, cap1, cap2];

  const relRef = useRef(null);
  const { scrollYProgress: relP } = useScroll({ target: relRef, offset: ["start end", "end start"] });
  const containerY = useTransform(relP, [0, 1], ["-9%", "9%"]);

  return (
    <div data-testid="home-page" className="bg-[#050505]">
      {/* CITY TICKER */}
      <div className="relative z-10 border-b border-white/10 bg-[#0a0a0a] py-3 mt-[72px]" data-testid="city-ticker">
        <Marquee speed={40} gradient={false} autoFill>
          <span className="text-white/40 text-xs tracking-[0.3em] mx-6">{CITIES}</span>
          <span className="text-[#FF3B30]/60 text-xs tracking-[0.3em] mx-6">·</span>
        </Marquee>
      </div>

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden" data-testid="hero-section">
        <motion.div style={{ opacity: heroTruckOpacity }} className="absolute right-0 top-1/2 -translate-y-1/2 w-[65%] max-w-[900px] pointer-events-none hidden md:block">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[115%] aspect-square rounded-full border border-[#FF3B30]/10 animate-[spin_30s_linear_infinite]" />
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[85%] aspect-square rounded-full border border-white/[0.04] animate-[spin_22s_linear_infinite_reverse]" />
          <div className="absolute inset-0 bg-[#FF3B30]/20 blur-[120px] rounded-full scale-75" />
          <motion.img
            src={IMG.truckHero} alt="Aportta"
            style={{ x: heroTruckX, scale: heroTruckScale }}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full object-contain"
          />
        </motion.div>
        <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 min-h-[82vh] flex flex-col justify-center py-24">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.8 }}
            className="text-[11px] sm:text-xs tracking-[0.28em] text-[#FF3B30] font-semibold mb-6 flex items-center gap-3"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-[#FF3B30]" /> {h.eyebrow}
          </motion.p>
          <motion.h1
            variants={lineParent} initial="hidden" animate="show"
            className="font-display font-black tracking-tighter leading-[0.9] text-white text-5xl sm:text-7xl lg:text-[6.5rem] max-w-3xl"
            data-testid="hero-title"
          >
            <span className="mask-line"><motion.span variants={lineChild} className="block">{h.h1_line1}</motion.span></span>
            <span className="mask-line"><motion.span variants={lineChild} className="block text-[#FF3B30]">{h.h1_line2}</motion.span></span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-8 text-white/65 text-base sm:text-lg max-w-md leading-relaxed"
            data-testid="hero-sub"
          >
            {h.sub}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-5"
          >
            <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5247] text-white text-xl sm:text-2xl font-black px-10 sm:px-12 py-5 sm:py-6 rounded-full transition-all duration-300 hover:scale-[1.03] shadow-[0_0_50px_rgba(255,59,48,0.5)]">
              {h.cta}
              <ArrowUpRight size={28} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
            <Link to="/services" data-testid="hero-secondary" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-base font-medium transition-colors duration-300">
              {h.cta_secondary} <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 bg-[#FF3B30] py-6 overflow-hidden" data-testid="marquee-section">
        <Marquee speed={48} gradient={false} autoFill>
          <span className="font-display font-black text-3xl sm:text-5xl tracking-tight text-black/90 mx-8">{h.marquee}</span>
          <span className="font-display font-black text-3xl sm:text-5xl tracking-tight text-black/20 mx-8">{h.marquee}</span>
        </Marquee>
      </section>

      {/* INTRO — own the outcome */}
      <section className="relative z-10 bg-[#050505]" data-testid="intro-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6 relative overflow-hidden rounded-3xl border border-white/10">
            <img src={IMG.highway} alt="" className="w-full h-[340px] md:h-[440px] object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
          </Reveal>
          <div className="lg:col-span-6">
            <Reveal>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl leading-[0.95] text-white">
                {h.intro_title_1}<br /><span className="text-white/45">{h.intro_title_2}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-7 text-white/60 text-base sm:text-lg leading-relaxed max-w-xl">{h.intro_body}</p>
            </Reveal>
            <Reveal delay={0.18}>
              <Link to="/about" className="group inline-flex items-center gap-2 mt-8 text-[#FF3B30] font-bold" data-testid="intro-link">
                {h.intro_link}
                <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS + SPEEDOMETER */}
      <section className="relative z-10 bg-[#0a0a0a] border-y border-white/10" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 md:py-24">
          <Reveal><p className="text-white/40 text-sm tracking-widest mb-12">{h.stats_kicker}</p></Reveal>
          <div className="grid lg:grid-cols-4 gap-12 items-center">
            {h.stats.slice(0, 3).map((s, i) => (
              <Reveal key={s.label} delay={i * 0.08} className="border-l-2 border-[#FF3B30]/60 pl-5">
                <p className="font-display font-black tracking-tighter text-5xl sm:text-6xl text-white">{s.value}</p>
                <p className="mt-2 text-white/50 text-sm">{s.label}</p>
              </Reveal>
            ))}
            <Speedometer label={h.speed_label} />
          </div>
        </div>
      </section>

      {/* PINNED DRIVING SHOWCASE */}
      <section ref={opsRef} className="relative bg-[#050505] hidden md:block" data-testid="driving-section" style={{ height: "260vh" }}>
        <div className="sticky top-0 h-screen overflow-hidden flex items-center">
          <motion.span style={{ x: wmX }} className="pointer-events-none absolute left-0 top-1/2 -translate-y-1/2 font-display font-black text-[24vw] leading-none text-white/[0.035] whitespace-nowrap select-none">
            HİZMETLER
          </motion.span>
          <div className="absolute left-1/2 -translate-x-1/2 top-0 h-full w-[3px] road-dashes opacity-40" />
          <motion.img
            src={IMG.truckTop} alt="" style={{ y: driveY, rotate: driveRotate }}
            className="absolute left-1/2 -translate-x-1/2 top-0 h-[80vh] w-auto object-contain drop-shadow-[0_30px_70px_rgba(0,0,0,0.8)]"
          />
          <div className="relative z-10 max-w-[1400px] mx-auto w-full px-5 sm:px-8">
            {t.services.items.slice(0, 3).map((it, i) => (
              <motion.div
                key={it.title}
                style={{ opacity: caps[i] }}
                className={`absolute top-1/2 -translate-y-1/2 max-w-xs ${i === 1 ? "right-8 text-right" : "left-8"}`}
              >
                <span className="font-display font-black text-6xl text-[#FF3B30] drop-shadow-[0_0_30px_rgba(255,59,48,0.5)]">0{i + 1}</span>
                <h3 className="mt-3 font-display font-bold text-4xl text-white">{it.title}</h3>
                <p className="mt-3 text-white/60 leading-relaxed">{it.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="relative z-10 bg-[#050505]" data-testid="services-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-2xl">
            <Reveal>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white leading-[0.95]">
                {h.services_title_1} <span className="text-white/45">{h.services_title_2}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}><p className="mt-6 text-white/55 text-base sm:text-lg leading-relaxed">{h.services_sub}</p></Reveal>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-3xl overflow-hidden">
            {t.services.items.map((item, i) => {
              const Icon = SERVICE_ICONS[i % SERVICE_ICONS.length];
              return (
                <Reveal key={item.title} delay={(i % 3) * 0.08} className="group bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#111] transition-colors duration-500" data-testid={`service-card-${i}`}>
                  <div className="w-12 h-12 rounded-xl bg-[#FF3B30]/10 border border-[#FF3B30]/20 flex items-center justify-center text-[#FF3B30] group-hover:bg-[#FF3B30] group-hover:text-white transition-all duration-500">
                    <Icon size={22} />
                  </div>
                  <h3 className="mt-7 font-display font-bold text-2xl text-white">{item.title}</h3>
                  <p className="mt-3 text-white/50 leading-relaxed">{item.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* RELIABILITY FEATURES (with container image) */}
      <section ref={relRef} className="relative z-10 bg-[#0a0a0a] border-y border-white/10" data-testid="reliability-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white leading-[0.95]">
                {h.reliability_title_1} <span className="text-[#FF3B30]">{h.reliability_title_2}</span>
              </h2>
            </Reveal>
            <Reveal delay={0.15} className="mt-10 relative overflow-hidden rounded-3xl border border-white/10 h-[340px]">
              <motion.img src={IMG.container} alt="" style={{ y: containerY }} className="absolute inset-x-0 -top-[10%] w-full h-[120%] object-cover" />
            </Reveal>
          </div>
          <div className="lg:col-span-7 flex flex-col divide-y divide-white/10">
            {h.features.map((f, i) => {
              const Icon = FEATURE_ICONS[i % FEATURE_ICONS.length];
              return (
                <Reveal key={f.t} delay={i * 0.1} className="py-8 flex gap-6 group">
                  <div className="shrink-0 w-12 h-12 rounded-xl border border-white/15 flex items-center justify-center text-[#FF3B30] group-hover:border-[#FF3B30]/60 transition-colors duration-500">
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-white">{f.t}</h3>
                    <p className="mt-2 text-white/55 leading-relaxed max-w-lg">{f.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative z-10 bg-[#050505]" data-testid="why-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal><p className="text-xs tracking-[0.28em] text-[#FF3B30] font-semibold mb-5">{h.why_kicker}</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white leading-[0.95] max-w-3xl">{h.why_title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {h.why.map((w, i) => (
              <Reveal key={w.t} delay={(i % 3) * 0.08} className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-8 hover:border-[#FF3B30]/40 transition-colors duration-500">
                <CheckCircle2 className="text-[#FF3B30]" size={24} />
                <h3 className="mt-5 font-display font-bold text-xl text-white">{w.t}</h3>
                <p className="mt-2 text-white/50 leading-relaxed text-sm">{w.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative z-10 bg-[#0a0a0a] border-y border-white/10" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal><p className="text-xs tracking-[0.28em] text-[#FF3B30] font-semibold mb-4">{h.testimonials_kicker}</p></Reveal>
          <Reveal delay={0.06}>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl text-white leading-[0.95] max-w-2xl">{h.testimonials_title}</h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {h.testimonials.map((tm, i) => (
              <Reveal key={tm.n} delay={i * 0.1} className="rounded-2xl border border-white/10 bg-[#050505] p-8 flex flex-col">
                <Quote className="text-[#FF3B30]" size={28} />
                <p className="mt-5 text-white/75 leading-relaxed flex-1">{tm.q}</p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <p className="font-display font-bold text-white">{tm.n}</p>
                  <p className="text-white/45 text-sm">{tm.r}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PARTNERS */}
      <section className="relative z-10 bg-[#050505]" data-testid="partners-section">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-20">
          <Reveal><p className="text-white/40 text-xs tracking-[0.28em] mb-10">{h.partners_kicker}</p></Reveal>
        </div>
        <div className="border-y border-white/10 py-8">
          <Marquee speed={35} gradient={false} autoFill>
            {h.partners.map((p) => (
              <span key={p} className="font-display font-bold text-xl md:text-2xl text-white/25 hover:text-white/60 transition-colors duration-300 mx-10 tracking-tight">{p}</span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative z-10 bg-[#050505]" data-testid="faq-section">
        <div className="max-w-[1000px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white">{h.faq_title}</h2>
          </Reveal>
          <Reveal delay={0.08}><p className="mt-4 text-white/55 text-lg">{h.faq_sub}</p></Reveal>
          <Reveal delay={0.14} className="mt-12">
            <Accordion type="single" collapsible className="w-full">
              {h.faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-white/10">
                  <AccordionTrigger className="text-left font-display font-bold text-lg md:text-xl text-white hover:text-[#FF3B30] hover:no-underline py-6" data-testid={`faq-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-white/55 text-base leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative z-10 bg-[#050505] pb-24 md:pb-36" data-testid="cta-band">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <Reveal className="relative overflow-hidden rounded-3xl border border-white/10">
            <img src={IMG.truckSide} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/50" />
            <div className="relative p-10 md:p-20 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
              <div className="max-w-xl">
                <h2 className="font-display font-black tracking-tighter text-4xl sm:text-6xl text-white leading-[0.95]">{h.final_title}</h2>
                <p className="mt-5 text-white/65 text-lg">{h.final_sub}</p>
              </div>
              <a href={APP_URL} target="_blank" rel="noopener noreferrer" data-testid="cta-band-button"
                className="group shrink-0 inline-flex items-center gap-3 bg-[#FF3B30] hover:bg-[#FF5247] text-white text-xl font-black px-10 py-6 rounded-full transition-all duration-300 hover:scale-[1.03] shadow-[0_0_50px_rgba(255,59,48,0.5)]">
                {h.cta}
                <ArrowUpRight size={26} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
